import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ReportingCategoryModel from "./add&updateReportingCategory";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const REPORTING_SORT_FIELDS = {
  name: "rC_Name",
  sortOrder: "rC_SortOrder",
  id: "rC_ID",
};

const REPORTING_SORT_LABELS = {
  [REPORTING_SORT_FIELDS.name]: "Name",
  [REPORTING_SORT_FIELDS.sortOrder]: "Sort Order",
  [REPORTING_SORT_FIELDS.id]: "ID",
};

const ManageReportingCategory = () => {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "reportingCategories";
  const defaultVisibleColumns = {
    name: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "rC_ID", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const categoriesFetchSeqRef = useRef(0);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [reportingCategoriesInUseIds, setReportingCategoriesInUseIds] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: REPORTING_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const fetchCategories = useCallback(async () => {
    const fetchSeq = ++categoriesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          compID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== categoriesFetchSeqRef.current) return;

      const categoryData = response.data.data || [];
      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, categoryData.length);
        }
      } else {
        setCategories([]);
      }
    } catch (error) {
      if (fetchSeq !== categoriesFetchSeqRef.current) return;
      setCategories([]);
      showErrorToast("Failed to fetch reporting categories.");
    } finally {
      if (fetchSeq === categoriesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [compID, search, sortBy, sortOrder]);

  const totalRecords = categories.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedCategories = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return categories.slice(start, start + size);
  }, [categories, pageNumber, pageSize]);

  const getReportingGlobalIndex = (category) =>
    categories.findIndex((c) => c.rC_ID === category.rC_ID);

  useDebouncedEffect(() => fetchCategories(), [fetchCategories]);

  const flattenCategories = (cats) => {
    if (!Array.isArray(cats)) return [];
    return cats.reduce((acc, c) => acc.concat([c], flattenCategories(c.subCategoriesList || [])), []);
  };

  const fetchReportingCategoriesInUse = useCallback(async () => {
    if (!compID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: { CompID: compID, SortBy: "RootCat_Name", SortOrder: "ASC", Search: "" },
      });
      const categoryData = response.data?.data || response.data || [];
      const flat = Array.isArray(categoryData) ? flattenCategories(categoryData) : [];
      const inUse = new Set(flat.map((c) => c.rootCat_Type ?? c.RootCat_Type).filter(Boolean));
      setReportingCategoriesInUseIds(inUse);
    } catch {
      setReportingCategoriesInUseIds(new Set());
    }
  }, [compID]);

  useEffect(() => {
    fetchReportingCategoriesInUse();
  }, [fetchReportingCategoriesInUse]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }
    
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(categoriesFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleCategorySubmit = async (categoryData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? APIs.PUT_UPDATE_CATEGORY_TYPE
        : APIs.POST_ADD_CATEGORY_TYPE;
      const method = isUpdate ? "put" : "post";
      const response = await axiosInstance[method](url, {
        ...categoryData,
        compID,
      });
      if (response.data.status) {
        const updatedCategory = response.data.data;
        if (isUpdate) {
          setCategories((prev) =>
            prev.map((cat) =>
              cat.rC_ID === updatedCategory.rC_ID ? updatedCategory : cat
            )
          );
        } else {
          setCategories((prev) => [...prev, updatedCategory]);
        }
        showSuccessToast(`Reporting Category ${isUpdate ? "Updated" : "Added"}.`);
      } else {
        // API Error silently
        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {
      // Error updating/adding category silently
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategoryClick = () => {
    setCategoryToEdit(null);
    setShowAddCategoryModal(true);
  };

  const handleEditCategoryClick = (category) => {
    setCategoryToEdit(category);
    setShowAddCategoryModal(true);
  };

  const DeleteCategory = async (category) => {
    if (reportingCategoriesInUseIds.has(category.rC_ID)) {
      await swal({
        title: "Cannot delete reporting category",
        text: `"${category.rC_Name}" is linked to one or more categories. Remove it from all categories first, then you can delete the reporting category.`,
        icon: "warning",
        button: "OK",
      });
      return;
    }

    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${category.rC_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_CATEGORY_TYPE, {
        params: {
          compID,
          RC_ID: category.rC_ID,
        },
      });
      if (response.data.status) {
        setCategories((prev) =>
          prev.filter((cat) => cat.rC_ID !== category.rC_ID)
        );
        fetchReportingCategoriesInUse();
        showSuccessToast("Reporting Category is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
      // Error silently
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (categoryId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (categories[i]?.rC_ID) {
            newSelected.add(categories[i].rC_ID);
          }
        }
      } else {
        if (newSelected.has(categoryId)) {
          newSelected.delete(categoryId);
        } else {
          newSelected.add(categoryId);
        }
        lastSelectedIndex.current = index;
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(categories.map((item) => item.rC_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No categories selected for deletion.");
      return;
    }

    const deletableIds = Array.from(selectedItems).filter((id) => !reportingCategoriesInUseIds.has(id));
    const inUseCount = selectedItems.size - deletableIds.length;
    if (inUseCount > 0 && deletableIds.length === 0) {
      await swal({
        title: "Cannot delete reporting categories",
        text: "All selected reporting category(ies) are linked to one or more categories. Remove them from categories first, then you can delete the reporting categories.",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: deletableIds.length < selectedItems.size
          ? `${inUseCount} selected reporting category(ies) are linked to categories and will be skipped. Delete ${deletableIds.length} reporting category(ies)?`
          : `Are you sure you want to delete ${deletableIds.length} selected reporting categories?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        deletableIds.map((categoryId) =>
          axiosInstance.delete(APIs.DELETE_CATEGORY_TYPE, {
            params: {
              compID,
              RC_ID: categoryId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCategories();
      fetchReportingCategoriesInUse();
      if (inUseCount > 0) {
        showSuccessToast(`${deletableIds.length} reporting category(ies) deleted. ${inUseCount} skipped (linked to categories).`);
      } else {
        showSuccessToast(`${deletableIds.length} reporting categories deleted successfully.`);
      }
    } catch (err) {
      showErrorToast("Failed to delete selected categories.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No categories selected for editing.");
      return;
    }

    const selectedCategories = categories.filter((cat) =>
      selectedItems.has(cat.rC_ID)
    );
    setCategoryToEdit(selectedCategories);
    setShowAddCategoryModal(true);
  };

  const handleDragStart = (e, category) => {
    const index = categories.findIndex((c) => c.rC_ID === category.rC_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newCategories = [...categories];
    const draggedCategory = newCategories[draggedIndex];
    newCategories.splice(draggedIndex, 1);
    newCategories.splice(overIndex, 0, draggedCategory);
    newCategories = newCategories.map((cat, index) => ({
      ...cat,
      rC_SortOrder: index + 1,
    }));

    setCategories(newCategories);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_CATEGORY_TYPE_SORT_ORDER, {
        compID: compID,
        Updates: newCategories.map((cat) => ({
          rC_ID: cat.rC_ID,
          rC_SortOrder: cat.rC_SortOrder,
        })),
      });
      fetchCategories();
      showSuccessToast("Category order updated successfully.");
    } catch (error) {
      // Error updating category order silently
      showErrorToast("Failed to update category order.");
      fetchCategories();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No reporting categories match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no reporting categories to show right now. Add a new category type or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || categories.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No reporting categories found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Reporting Categorie Types </li>
          </ol>
          <h1 className="page-header mb-0">Manage Reporting Categorie Types</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddCategoryClick}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Reporting Category Type
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddCategoryClick} title="Add Reporting Category Type" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Reporting Categories"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                columnOrder={["name"]}
                columnLabels={{ name: "Name" }}
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedCategories.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" />
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={REPORTING_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCategories.map((category) => {
                          const globalIndex = getReportingGlobalIndex(category);
                          return (
                            <tr
                              key={category.rC_ID}
                              draggable
                              onDragStart={(e) => handleDragStart(e, category)}
                              onDragEnter={(e) => handleDragEnter(e, globalIndex)}
                              onDragLeave={handleDragLeave}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              onDragEnd={handleDragEnd}
                            >
                              <td className="align-middle">
                                <i
                                  className="fas fa-bars text-secondary"
                                  style={{ cursor: "grab" }}
                                />
                              </td>
                              {visibleColumns.name && (
                                <td
                                  className="align-middle"
                                  onClick={() => handleEditCategoryClick(category)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {category.rC_Name}
                                </td>
                              )}
                              <td className="align-middle text-center">
                                <TableActionsMenu>
                                  <TableActionsItem
                                    icon="fa fa-edit"
                                    onClick={() => handleEditCategoryClick(category)}
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => DeleteCategory(category)}
                                    disabled={reportingCategoriesInUseIds.has(category.rC_ID)}
                                    title={
                                      reportingCategoriesInUseIds.has(category.rC_ID)
                                        ? "Used in categories – cannot delete"
                                        : undefined
                                    }
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <ReportingCategoryModel
        isModalOpen={showAddCategoryModal}
        setIsModalOpen={setShowAddCategoryModal}
        onCategorySubmit={handleCategorySubmit}
        compID={compID}
        categoryToEdit={categoryToEdit}
      />
    </div>
  );
};

export default ManageReportingCategory;